import React, { useState, useEffect, useRef } from 'react';
import './Notes.css';

const Notes = ({ character, updateCharacter }) => {
    // Local state for immediate feedback
    const [journal, setJournal] = useState(character.notes || '');
    const [backstory, setBackstory] = useState(character.backstory || '');
    const [imageUrl, setImageUrl] = useState(character.backstoryImage || '');
    const [rankingSave, setRankingSave] = useState(false);
    const timeoutRef = useRef(null);

    // Sync from props if character changes externally and we aren't typing
    useEffect(() => {
        if (!rankingSave) {
            setJournal(character.notes || '');
            setBackstory(character.backstory || '');
            setImageUrl(character.backstoryImage || '');
        }
    }, [character.notes, character.backstory, character.backstoryImage, rankingSave]);

    const handleSave = (field, value) => {
        // Debounce save
        if (timeoutRef.current) clearTimeout(timeoutRef.current);

        setRankingSave(true);
        timeoutRef.current = setTimeout(() => {
            updateCharacter([field], value);
            setRankingSave(false);
        }, 1000);
    };

    const handleChange = (field, value) => {
        if (field === 'notes') setJournal(value);
        if (field === 'backstory') setBackstory(value);

        handleSave(field, value);
    };

    const handleBlur = (field, value) => {
        if (timeoutRef.current) clearTimeout(timeoutRef.current);
        updateCharacter([field], value);
        setRankingSave(false);
    };

    const handleImageUpload = (e) => {
        const file = e.target.files[0];
        if (!file) return;

        // Basic validation
        if (!file.type.startsWith('image/')) {
            alert('Please select an image file.');
            return;
        }

        if (file.size > 10 * 1024 * 1024) { // 10MB limit
            alert('Image size exceeds 10MB limit.');
            return;
        }

        const reader = new FileReader();
        reader.onloadend = () => {
            const base64String = reader.result;
            setImageUrl(base64String);
            updateCharacter(['backstoryImage'], base64String);
        };
        reader.readAsDataURL(file);
    };

    const handleRemoveImage = () => {
        setImageUrl('');
        updateCharacter(['backstoryImage'], '');
    };

    return (
        <div className="notes-container">
            <div className="notes-header-area">
                <h2>Notes & Backstory</h2>
                {rankingSave && <span className="notes-status">Saving...</span>}
            </div>

            <div className="notes-grid">
                {/* Left Column: Background/Backstory */}
                <div className="notes-column backstory-col">
                    <h3>Backstory</h3>
                    <div className="notes-wrapper">
                        <div className="notes-image-section">
                            {imageUrl ? (
                                <div className="notes-image-display">
                                    <img src={imageUrl} alt="Character Backstory" />
                                    <button className="remove-image-btn" onClick={handleRemoveImage}>Remove</button>
                                </div>
                            ) : (
                                <div className="notes-image-input">
                                    <label className="upload-btn">
                                        Upload Image
                                        <input
                                            type="file"
                                            accept="image/*"
                                            onChange={handleImageUpload}
                                            style={{ display: 'none' }}
                                        />
                                    </label>
                                    <span className="image-help">Max 10MB (Stored Locally)</span>
                                </div>
                            )}
                        </div>
                        <textarea
                            className="notes-textarea"
                            value={backstory}
                            onChange={(e) => handleChange('backstory', e.target.value)}
                            onBlur={(e) => handleBlur('backstory', e.target.value)}
                            placeholder="Character backstory, history, and origins..."
                            spellCheck="false"
                        />
                    </div>
                </div>

                {/* Right Column: Campaign Journal */}
                <div className="notes-column journal-col">
                    <h3>Campaign Journal</h3>
                    <div className="notes-wrapper">
                        <textarea
                            className="notes-textarea"
                            value={journal}
                            onChange={(e) => handleChange('notes', e.target.value)}
                            onBlur={(e) => handleBlur('notes', e.target.value)}
                            placeholder="Quest logs, NPC names, and session notes..."
                            spellCheck="false"
                        />
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Notes;
